/* ============================================================
   TUTORIAL  (/tutorial)  — SESAAT Studio v3.9
   ============================================================
   Rupa mengikut halaman Formulas (FormulaShelfView) — komponen
   kongsi design/ui.jsx dan token --s-* sahaja. Tiada warna
   ditulis terus, tiada gradien, tiada emoji.

   ⛔ Warna jenama (--s-brand) TIDAK digunakan untuk kawalan.
      Keadaan terpilih/aktif guna --s-ink / --s-select, ikut
      peraturan dalam design/tokens.css. Lencana BARU ialah satu-
      satunya tempat --s-brand muncul di sini.

   SUMBER KANDUNGAN = Firestore `config/tutorial`. Admin tekan
   "Edit halaman" → seluruh halaman jadi borang → "Simpan" menulis
   SEKALI (satu tulisan atomik). Corak sama dengan What's New,
   jadi tambah video baharu TIDAK perlu deploy.

   KOS: video duduk di YouTube, thumbnail pun dari YouTube, dan
   progress tontonan dalam localStorage. Halaman ini hanya 1
   bacaan Firestore setiap lawatan — tiada kos Storage langsung.

   ⚠️ Video TANPA link YouTube disorok daripada pelanggan; admin
      tetap nampak supaya boleh isi linknya. Kad yang tidak boleh
      dimainkan hanya mengelirukan orang yang datang untuk belajar.
   ============================================================ */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { getUserRole, canAccessAdmin } from '../utils/roles';
import {
    Search, Play, Check, Circle, CircleCheck, X, Plus, Pencil, Trash2,
    ChevronUp, ChevronDown, ArrowRight, Youtube, MessageCircle, ExternalLink, ListVideo,
} from 'lucide-react';
import {
    TUTORIAL_DOC, loadTutorial, clearTutorialCache, normalizeTutorial,
    newVideo, newCategory, parseYouTubeId, youtubeThumb, buildChapters, alamatLesson,
    getWatched, markWatched, unmarkWatched,
} from '../data/tutorial';

import '../design/tokens.css';
import { Badge, Btn, IconBtn, Card, CardBody, CardFoot, PageHeader, Segmented, Field, EmptyState } from '../design/ui';
import TutorialShell from './TutorialShell';
import { useT } from '../i18n';

/* Dalam mod edit, medan link menyimpan apa sahaja yang admin taip (termasuk
   teks separuh siap). Sentiasa tapis melalui parseYouTubeId sebelum guna,
   supaya teks yang belum sah tidak menghasilkan thumbnail atau embed rosak. */
const ytOf = (v) => parseYouTubeId(v?.youtubeId);

/* ---------- Kepala seksyen ---------------------------------------- */
function SectionHead({ title, desc }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--s-ink)', letterSpacing: '-0.01em' }}>{title}</h2>
            {desc && <p className="s-meta" style={{ marginTop: 3 }}>{desc}</p>}
        </div>
    );
}

/* ---------- Penerangan lesson (boleh sangat panjang) ---------------
   ⭐ `pre-wrap` — admin menaip teks ini dalam textarea, jadi baris baharu
   yang dia tekan MEMANG sebahagian daripada kandungan (senarai langkah,
   blok JSON contoh). Tanpa ia, HTML meleburkan setiap baris menjadi satu
   ruang dan lesson keluar sebagai satu perenggan panjang.

   ⭐ Kenapa ada butang "See more" DI SINI tetapi TIADA pada halaman lesson:
   halaman senarai memapar belasan lesson bersebelahan — satu teks 900 aksara
   menolak semua lesson lain keluar dari skrin. Halaman lesson pula memapar
   SATU lesson sahaja, dan di situ user memang mahu teks penuh untuk disalin
   (keputusan user 2026-08-22). Jadi teks TIDAK PERNAH dipotong secara kekal;
   ia cuma dilipat, dan sekali dibuka ia penuh serta boleh disalin.

   Ambang: lebih 3 baris ATAU lebih 200 aksara. Penerangan biasa (satu ayat)
   kekal seperti dahulu — tiada butang, tiada perubahan rupa. */
function TeksLesson({ teks, t, style }) {
    const [buka, setBuka] = useState(false);
    const isi = String(teks || '');
    if (!isi.trim()) return null;
    const panjang = isi.split('\n').length > 3 || isi.length > 200;
    return (
        <div style={style}>
            <p
                className="s-meta"
                style={{
                    lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: 0,
                    ...(panjang && !buka
                        ? { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
                        : null),
                }}
            >{isi}</p>
            {panjang && (
                <button
                    type="button"
                    /* stopPropagation — kad induk pada sesetengah susunan boleh
                       dibuka dengan klik; tanpa ini "See more" turut membuka video. */
                    onClick={(e) => { e.stopPropagation(); setBuka(v => !v); }}
                    style={{
                        marginTop: 4, padding: 0, border: 0, background: 'none', cursor: 'pointer',
                        font: 'inherit', fontSize: 12, fontWeight: 600,
                        color: 'var(--s-ink-2)', textDecoration: 'underline', textUnderlineOffset: 3,
                    }}
                >
                    {buka ? t('tutorial.btn.seeLess') : t('tutorial.btn.seeMore')}
                </button>
            )}
        </div>
    );
}

/* ---------- Medan borang mod edit --------------------------------
   Bingkai BERTITIK — bahasa yang sama dengan petak "Create your own"
   pada halaman Formulas, iaitu "belum ada, tekan untuk isi". */
function EditBox({ value, onChange, placeholder = '', rows = 0, mono = false, style }) {
    const asas = {
        width: '100%', boxSizing: 'border-box',
        background: 'var(--s-ground)',
        border: '1px dashed var(--s-line-strong)',
        borderRadius: 'var(--s-r)',
        padding: '8px 10px',
        color: 'var(--s-ink)',
        fontSize: 13,
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
        outline: 'none',
        ...style,
    };
    const fokus = (e) => { e.target.style.borderStyle = 'solid'; e.target.style.borderColor = 'var(--s-ink-3)'; };
    const lepas = (e) => { e.target.style.borderStyle = 'dashed'; e.target.style.borderColor = 'var(--s-line-strong)'; };

    return rows > 0
        ? <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder}
            style={{ ...asas, resize: 'vertical' }} onFocus={fokus} onBlur={lepas} />
        : <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
            style={asas} onFocus={fokus} onBlur={lepas} />;
}

/* Grid kad video. 340px memberi TIGA lajur pada lebar panel biasa —
   ketumpatan yang SAMA dengan halaman Formulas. Ditulis sebagai auto-fill
   dan bukan `repeat(3, …)` supaya ia runtuh sendiri pada skrin sempit;
   thumbnail 16:9 pada lajur selebar 1/3 skrin sudah cukup besar dibaca. */
const GRID_KAD = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 };

const selectStyle = {
    background: 'var(--s-ground)', color: 'var(--s-ink)',
    border: '1px dashed var(--s-line-strong)', borderRadius: 'var(--s-r)',
    padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none',
};

/* ---------- Pratonton video ---------------------------------------
   Bingkai 16:9 — bentuk video YouTube itu sendiri. Bila belum ada
   link, ia kekal bingkai kosong (bukan warna rekaan), sama seperti
   kad formula tanpa video contoh. */
function Thumb({ ytId, done, duration }) {
    const t = useT();
    const url = youtubeThumb(ytId);
    return (
        <div style={{
            position: 'relative', aspectRatio: '16 / 9', overflow: 'hidden',
            background: 'var(--s-ground)', borderBottom: '1px solid var(--s-line)',
            display: 'grid', placeItems: 'center',
        }}>
            {url && (
                <img
                    src={url} alt="" loading="lazy"
                    style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        objectFit: 'cover', opacity: done ? 0.4 : 1, transition: 'opacity .15s ease',
                    }}
                />
            )}

            {ytId ? (
                <span className="s-fcard__play" style={{ position: 'relative' }}>
                    <Play size={14} strokeWidth={0} fill="currentColor" />
                </span>
            ) : (
                <span className="s-meta" style={{ position: 'relative' }}>{t('tutorial.thumb.noVideo')}</span>
            )}

            {done && <span className="s-fcard__tick"><Check size={12} strokeWidth={2.8} /></span>}
            {duration && <span className="s-fcard__dur">{duration}</span>}
        </div>
    );
}

export default function TutorialView() {
    const t = useT();
    const isAdmin = canAccessAdmin(getUserRole(auth.currentUser?.email));

    const [data, setData] = useState(() => normalizeTutorial(null));   // dipapar
    const [draft, setDraft] = useState(null);                          // salinan sedang diedit
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [watched, setWatched] = useState(() => getWatched());
    const [activeCat, setActiveCat] = useState('all');
    const [cari, setCari] = useState('');

    const navigate = useNavigate();
    const editing = draft !== null;
    const view = editing ? draft : data;

    useEffect(() => {
        let hidup = true;
        (async () => {
            try {
                const { data: dibaca } = await loadTutorial();
                if (hidup) setData(dibaca);
            } catch (e) {
                console.warn('[Tutorial] gagal baca, guna salinan simpanan:', e);
            } finally {
                if (hidup) setLoading(false);
            }
        })();
        return () => { hidup = false; };
    }, []);

    // ── Mod edit ──────────────────────────────────────
    const startEdit = () => { setError(null); setDraft(JSON.parse(JSON.stringify(data))); };
    const cancelEdit = () => { setDraft(null); setError(null); };

    const saveEdit = async () => {
        if (!draft) return;
        setSaving(true);
        setError(null);
        try {
            const bersih = normalizeTutorial(draft);
            await setDoc(doc(db, 'config', TUTORIAL_DOC), bersih);
            setData(bersih);
            clearTutorialCache();
            setDraft(null);
        } catch (e) {
            console.error('[Tutorial] simpan gagal:', e);
            /* {ralat} = kod/mesej mentah Firebase — dihantar sebagai
               pemboleh ubah, tidak pernah diterjemah. */
            setError(t('tutorial.err.saveFailed', { ralat: e?.code || e?.message || t('tutorial.err.unknown') }));
        } finally {
            setSaving(false);
        }
    };

    const mutate = (fn) => setDraft(d => { const salin = JSON.parse(JSON.stringify(d)); fn(salin); return salin; });
    const setVideo = (i, key, val) => mutate(d => { d.videos[i][key] = val; });
    const moveItem = (arr, dari, ke) => { if (ke < 0 || ke >= arr.length) return; const [it] = arr.splice(dari, 1); arr.splice(ke, 0, it); };

    /* ── Tontonan ──────────────────────────────────────
       Video dibuka pada halamannya sendiri (/tutorial/:videoId), BUKAN
       dalam modal. Modal menutup dirinya dan mencampak pelanggan balik
       ke grid selepas setiap video; halaman lesson membawa rel kurikulum
       dan butang "Lesson seterusnya", jadi satu bab boleh dihabiskan
       dalam satu duduk. Halaman ini kekal sebagai overview + tempat
       admin mengedit kandungan. */
    const openVideo = (v) => {
        if (editing || !ytOf(v)) return;
        navigate(alamatLesson(v, view));
    };

    /* Rel kurikulum boleh diklik walaupun mod edit sedang terbuka —
       jadi ia mesti bertanya dahulu. Tanpa ini, satu klik pada rel
       membuang seluruh suntingan admin tanpa sebarang amaran. */
    const bolehTinggalkanEdit = () =>
        !editing || window.confirm(t('tutorial.confirm.leaveEdit'));

    const toggleWatched = (e, v) => {
        e.stopPropagation();
        setWatched(watched.has(v.id) ? unmarkWatched(v.id) : markWatched(v.id));
    };

    // ── Penapisan ─────────────────────────────────────
    const visibleVideos = useMemo(
        () => (view.videos || []).filter(v => isAdmin || ytOf(v)),
        [view.videos, isAdmin]
    );

    const ditapis = useMemo(() => {
        const q = cari.trim().toLowerCase();
        return visibleVideos.filter(v => {
            const okKategori = activeCat === 'all' || v.category === activeCat;
            const okCari = !q || `${v.title} ${v.body}`.toLowerCase().includes(q);
            return okKategori && okCari;
        });
    }, [visibleVideos, activeCat, cari]);

    const countFor = (catId) => (catId === 'all'
        ? visibleVideos.length
        : visibleVideos.filter(v => v.category === catId).length);

    /* ── Progress ──────────────────────────────────────
       Kiraan penuh (bar + nisbah) hidup dalam rel TutorialShell. Di sini
       ia hanya menentukan perkataan pada butang kepala: "Start watching"
       bagi orang baharu, "Continue watching" bagi yang sudah bermula. */
    const watchedCount = visibleVideos.filter(v => watched.has(v.id)).length;

    const steps = useMemo(
        () => (view.startHere || []).map(id => view.videos.find(v => v.id === id)).filter(Boolean),
        [view.startHere, view.videos]
    );

    /* Langkah SEMASA dalam timeline "Mula Sini" — langkah pertama yang
       ada video tetapi belum ditonton. Hanya SATU langkah dibuka sebagai
       kad; -1 bermakna semuanya sudah selesai dan tiada kad dibuka. */
    const idxSemasa = steps.findIndex(v => ytOf(v) && !watched.has(v.id));

    /* Bab = kategori. Susunan dikira oleh buildChapters — fungsi yang
       SAMA dipakai halaman lesson, supaya "Bab 2" di sini bermaksud
       "Bab 2" di sana juga. */
    const chapters = useMemo(
        () => buildChapters(view, { onlyPlayable: !isAdmin }),
        [view, isAdmin]
    );

    // Utamakan langkah "Mula Sini" — itu turutan yang kami mahu diikut dahulu.
    const nextVideo = useMemo(() => {
        const turutan = [...steps, ...visibleVideos];
        return turutan.find(v => ytOf(v) && !watched.has(v.id)) || null;
    }, [steps, visibleVideos, watched]);

    /* ⛔ HANYA label yang diterjemah — `value: 'all'` dan `c.id`
       dibandingkan dengan activeCat/v.category, jadi nilainya kekal. */
    const catOptions = useMemo(() => ([
        { value: 'all', label: <>{t('tutorial.filter.all')} <span style={{ opacity: 0.5 }}>{countFor('all')}</span></> },
        ...(view.categories || []).map(c => ({
            value: c.id,
            label: <>{c.label || t('tutorial.untitledCategory')} <span style={{ opacity: 0.5 }}>{countFor(c.id)}</span></>,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ]), [view.categories, visibleVideos, t]);

    if (loading) {
        return (
            <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>
                <EmptyState title={t('tutorial.loading.title')}>{t('tutorial.loading.body')}</EmptyState>
            </div>
        );
    }

    /* Butang kepala halaman. Ia dihantar ke TutorialShell, yang melukis
       kepala sebagai KAWASAN SENDIRI merentas penuh (bentuk /workspace) —
       jadi tajuk, ayat pengenalan dan butang duduk pada satu baris di atas
       rel dan kandungan, bukan di dalam salah satu daripadanya. */
    const tindakanKepala = (
        <>
            {nextVideo && !editing && (
                <Btn onClick={() => openVideo(nextVideo)} icon={<Play size={14} strokeWidth={0} fill="currentColor" />}>
                    {watchedCount === 0 ? t('tutorial.btn.startWatching') : t('tutorial.btn.continueWatching')}
                </Btn>
            )}

            {isAdmin && (editing ? (
                <>
                    <Btn onClick={saveEdit} disabled={saving}>{saving ? t('tutorial.btn.saving') : t('tutorial.btn.save')}</Btn>
                    <Btn variant="ghost" onClick={cancelEdit} disabled={saving}>{t('tutorial.btn.cancel')}</Btn>
                    <Btn
                        variant="ghost"
                        icon={<Plus size={14} strokeWidth={2} />}
                        onClick={() => mutate(d => d.videos.unshift(newVideo(d.categories[0]?.id || '')))}
                    >
                        {t('tutorial.btn.newVideo')}
                    </Btn>
                </>
            ) : (
                <Btn variant="ghost" icon={<Pencil size={14} strokeWidth={1.9} />} onClick={startEdit}>
                    {t('tutorial.btn.editPage')}
                </Btn>
            ))}
        </>
    );

    return (
        /* Rangka yang SAMA dengan halaman lesson: kepala penuh di atas, rel
           kurikulum di kiri, kandungan di kanan. Overview ialah item pertama
           rel itu, jadi pelanggan tidak pernah terkeluar daripada rel
           sepanjang dia berada dalam Tutorial (keputusan user 2026-08-16). */
        <TutorialShell
            chapters={chapters}
            data={view}
            watched={watched}
            aktif="overview"
            bolehPergi={bolehTinggalkanEdit}
            subtajuk={editing ? undefined : view.subtitle}
            tindakan={tindakanKepala}
        >

            {/* Baris maklumat mod edit — sengaja di luar PageHeader supaya
                ia tidak menyempitkan tajuk pada skrin sederhana. */}
            {editing && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                    <Badge tone="warn">{t('tutorial.edit.badge')}</Badge>
                    <span className="s-meta">{t('tutorial.edit.hint')}</span>
                </div>
            )}
            {error && (
                <div style={{ marginBottom: 16 }}>
                    <Badge tone="stop">{error}</Badge>
                </div>
            )}
            {editing && (
                <div style={{ marginBottom: 20, maxWidth: 560 }}>
                    <p className="s-dim" style={{ display: 'block', marginBottom: 6 }}>{t('tutorial.edit.subtitleLabel')}</p>
                    {/* NILAI subtitle = kandungan Firestore; hanya placeholder diterjemah. */}
                    <EditBox value={view.subtitle} onChange={(v) => mutate(d => { d.subtitle = v; })}
                        placeholder={t('tutorial.edit.subtitlePlaceholder')} />
                </div>
            )}

            {/* ─────────── MULA SINI ─────────── */}
            {(steps.length > 0 || editing) && (
                <section style={{ marginBottom: 30 }}>
                    <SectionHead
                        title={t('tutorial.startHere.title')}
                        desc={t('tutorial.startHere.desc')}
                    />

                    {editing && (
                        <div style={{
                            marginBottom: 12, padding: 14,
                            border: '1px dashed var(--s-line-strong)', borderRadius: 'var(--s-r-lg)',
                            background: 'var(--s-card)',
                        }}>
                            <p className="s-dim" style={{ display: 'block', marginBottom: 8 }}>{t('tutorial.edit.startHereOrder')}</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {(view.startHere || []).map((id, k) => {
                                    const v = view.videos.find(x => x.id === id);
                                    return (
                                        <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span className="s-meta" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {k + 1}. {v?.title || t('tutorial.untitledLesson')}
                                            </span>
                                            <IconBtn label={t('tutorial.a11y.moveUp')} icon={<ChevronUp size={15} strokeWidth={1.9} />}
                                                onClick={() => mutate(d => moveItem(d.startHere, k, k - 1))} />
                                            <IconBtn label={t('tutorial.a11y.moveDown')} icon={<ChevronDown size={15} strokeWidth={1.9} />}
                                                onClick={() => mutate(d => moveItem(d.startHere, k, k + 1))} />
                                            <IconBtn label={t('tutorial.a11y.removeStep')} icon={<X size={15} strokeWidth={1.9} />}
                                                onClick={() => mutate(d => d.startHere.splice(k, 1))} />
                                        </div>
                                    );
                                })}
                                <select
                                    value=""
                                    onChange={(e) => { const id = e.target.value; if (id) mutate(d => d.startHere.push(id)); }}
                                    style={{ ...selectStyle, marginTop: 4, maxWidth: 320 }}
                                >
                                    {/* ⛔ value="" kekal kosong — ia penanda "belum pilih". */}
                                    <option value="">{t('tutorial.edit.addStep')}</option>
                                    {view.videos
                                        .filter(v => !(view.startHere || []).includes(v.id))
                                        .map(v => <option key={v.id} value={v.id}>{v.title || t('tutorial.untitledLesson')}</option>)}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* ── TIMELINE ──────────────────────────────────────
                        Bukan senarai rata: garis menegak menyambung langkah,
                        dan SATU langkah sahaja terbuka sebagai kad — langkah
                        tempat pelanggan berhenti. Selebihnya baris nipis, jadi
                        mata terus jatuh ke tempat yang betul tanpa membaca
                        semuanya.

                        Warna garis membawa maklumat: segmen di bawah langkah
                        yang SUDAH ditonton dipekatkan, jadi kemajuan kelihatan
                        walaupun tanpa membaca nombor. */}
                    {steps.length > 0 && (
                        <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {steps.map((v, k) => {
                                const done = watched.has(v.id);
                                const ytId = ytOf(v);
                                const semasa = k === idxSemasa;
                                const akhir = k === steps.length - 1;
                                const buka = () => openVideo(v);

                                return (
                                    <li key={v.id} style={{
                                        position: 'relative',
                                        display: 'grid', gridTemplateColumns: '30px minmax(0, 1fr)', gap: 14,
                                        paddingBottom: akhir ? 0 : 18,
                                    }}>
                                        {!akhir && (
                                            <span style={{
                                                position: 'absolute', left: 14, top: 34, bottom: -2, width: 1,
                                                background: done ? 'var(--s-ink-3)' : 'var(--s-line)',
                                            }} />
                                        )}

                                        {/* Bulatan keadaan: siap · sedang di sini · belum */}
                                        <span style={{
                                            width: 30, height: 30, borderRadius: 99, flex: 'none',
                                            display: 'grid', placeItems: 'center',
                                            fontSize: 12, fontWeight: 600,
                                            background: done ? 'var(--s-select)' : 'transparent',
                                            color: done ? 'var(--s-select-ink)' : semasa ? 'var(--s-ink)' : 'var(--s-ink-3)',
                                            border: done ? 'none'
                                                : semasa ? '2px solid var(--s-ink)' : '1px solid var(--s-line-strong)',
                                        }}>
                                            {done ? <Check size={14} strokeWidth={2.8} />
                                                : semasa ? <Play size={12} strokeWidth={0} fill="currentColor" />
                                                    : k + 1}
                                        </span>

                                        {semasa ? (
                                            <Card>
                                                <CardBody>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                                                        <h3 className="s-cardname">{v.title || t('tutorial.untitledLesson')}</h3>
                                                        <Badge tone="brand">{t('tutorial.badge.current')}</Badge>
                                                    </div>
                                                    <TeksLesson teks={v.body} t={t} style={{ marginBottom: 12 }} />
                                                    <Btn size="sm" icon={<Play size={13} strokeWidth={0} fill="currentColor" />} onClick={buka}>
                                                        {k === 0 ? t('tutorial.btn.startWatching') : t('tutorial.btn.continueWatching')}
                                                        {v.duration ? ` · ${v.duration}` : ''}
                                                    </Btn>
                                                </CardBody>
                                            </Card>
                                        ) : (
                                            <div
                                                role="button"
                                                tabIndex={ytId ? 0 : -1}
                                                aria-disabled={!ytId}
                                                onClick={buka}
                                                onKeyDown={(e) => { if (ytId && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); buka(); } }}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 12,
                                                    minHeight: 30, paddingTop: 3,
                                                    cursor: ytId ? 'pointer' : 'default',
                                                }}
                                            >
                                                <span style={{ flex: 1, minWidth: 0 }}>
                                                    <span className="s-cardname" style={{ display: 'block', color: done ? 'var(--s-ink-2)' : undefined }}>
                                                        {v.title || t('tutorial.untitledLesson')}
                                                    </span>
                                                    <span className="s-meta" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {v.body}
                                                    </span>
                                                </span>

                                                <span className="s-dim" style={{ flex: 'none' }}>
                                                    {!ytId ? t('tutorial.state.comingSoon') : done ? t('tutorial.btn.rewatch') : t('tutorial.btn.watch')}
                                                    {v.duration ? ` · ${v.duration}` : ''}
                                                </span>
                                                {ytId && <ArrowRight size={15} strokeWidth={1.9} style={{ color: 'var(--s-ink-3)', flex: 'none' }} />}
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </section>
            )}

            {/* ─────────── BAB ───────────
                Kurikulum, bukan senarai. Kad bab ialah pintu masuk yang
                sebenar: ia menghantar pelanggan terus ke lesson yang dia
                belum tonton, dan dari situ butang "Lesson seterusnya"
                membawanya sampai habis bab. Grid di bawah kekal sebagai
                laluan mencari video tertentu.

                Disorok dalam mod edit — bar progress dan kiraan di sini
                dikira, bukan diedit; admin mengedit kategori dalam petak
                "Kategori" di bawah. */}
            {!editing && chapters.length > 0 && (
                <section style={{ marginBottom: 30 }}>
                    <SectionHead
                        title={t('tutorial.chapters.title')}
                        desc={t('tutorial.chapters.desc')}
                    />

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 14 }}>
                        {chapters.map((bab, k) => {
                            const jumlah = bab.videos.length;
                            const siap = bab.videos.filter(v => watched.has(v.id)).length;
                            const pct = jumlah ? Math.round((siap / jumlah) * 100) : 0;
                            const habis = siap >= jumlah;
                            /* Masuk pada lesson pertama yang belum ditonton — itu
                               tempat pelanggan berhenti kali terakhir. Bila semua
                               sudah ditonton, kembali ke lesson pertama.
                               `bab.videos` sudah ditapis mengikut siapa yang
                               melihat (pelanggan tidak pernah dapat lesson tanpa
                               video), jadi tiada semakan link diperlukan di sini. */
                            const masuk = bab.videos.find(v => !watched.has(v.id)) || bab.videos[0];
                            const buka = () => { if (masuk) navigate(alamatLesson(masuk, view)); };

                            return (
                                <Card
                                    key={bab.id}
                                    hover={!!masuk}
                                    role="button"
                                    tabIndex={masuk ? 0 : -1}
                                    aria-disabled={!masuk}
                                    onClick={buka}
                                    onKeyDown={(e) => { if (masuk && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); buka(); } }}
                                    style={{ cursor: masuk ? 'pointer' : 'default' }}
                                >
                                    <CardBody>
                                        <span className="s-dim" style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', marginBottom: 5 }}>
                                            {t('tutorial.chapters.eyebrow', { nombor: k + 1 })}
                                        </span>
                                        <h3 className="s-cardname" style={{ marginBottom: 12 }}>{bab.label}</h3>

                                        <div style={{ height: 4, borderRadius: 99, background: 'var(--s-fill)', overflow: 'hidden', marginBottom: 9 }}>
                                            <div style={{ height: '100%', width: `${pct}%`, background: 'var(--s-ink)', borderRadius: 99, transition: 'width .4s ease' }} />
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span className="s-meta" style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                                                {/* Enjin i18n tiada sokongan plural terbina — pemanggil
                                                    yang memilih kunci tunggal/jamak. */}
                                                {t(jumlah === 1 ? 'tutorial.chapters.countOne' : 'tutorial.chapters.countMany', { jumlah, siap })}
                                            </span>
                                            <span className="s-dim" style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 'none' }}>
                                                {habis ? t('tutorial.btn.rewatch') : siap > 0 ? t('tutorial.btn.continue') : t('tutorial.btn.start')}
                                                {masuk && <ArrowRight size={14} strokeWidth={1.9} />}
                                            </span>
                                        </div>
                                    </CardBody>
                                </Card>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* ─────────── SEMUA TUTORIAL ─────────── */}
            <section>
                <SectionHead title={t('tutorial.all.title')} desc={t('tutorial.all.desc')} />

                {editing && (
                    <div style={{
                        marginBottom: 14, padding: 14,
                        border: '1px dashed var(--s-line-strong)', borderRadius: 'var(--s-r-lg)',
                        background: 'var(--s-card)',
                    }}>
                        <p className="s-dim" style={{ display: 'block', marginBottom: 8 }}>{t('tutorial.chapters.title')}</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                            {(view.categories || []).map((c, k) => (
                                <span key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <EditBox
                                        value={c.label}
                                        onChange={(val) => mutate(d => { d.categories[k].label = val; })}
                                        placeholder={t('tutorial.edit.chapterName')}
                                        style={{ width: 140 }}
                                    />
                                    <IconBtn
                                        label={t('tutorial.a11y.deleteChapter', { nama: c.label || '' })}
                                        icon={<X size={15} strokeWidth={1.9} />}
                                        onClick={() => mutate(d => {
                                            const [gone] = d.categories.splice(k, 1);
                                            // Video kategori tersebut dipindah ke kategori pertama supaya
                                            // ia tidak lenyap daripada semua penapis.
                                            const ganti = d.categories[0]?.id || '';
                                            d.videos.forEach(v => { if (v.category === gone.id) v.category = ganti; });
                                        })}
                                    />
                                </span>
                            ))}
                            <Btn size="sm" variant="ghost" icon={<Plus size={14} strokeWidth={2} />}
                                onClick={() => mutate(d => d.categories.push(newCategory()))}>
                                {t('tutorial.edit.addChapter')}
                            </Btn>
                        </div>
                    </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
                    <Segmented value={activeCat} onChange={setActiveCat} options={catOptions} />
                    <Field
                        icon={<Search size={15} strokeWidth={1.9} />}
                        placeholder={t('tutorial.search.placeholder')}
                        value={cari}
                        onChange={(e) => setCari(e.target.value)}
                        style={{ maxWidth: 280, marginLeft: 'auto' }}
                    />
                </div>

                {ditapis.length === 0 ? (
                    <EmptyState title={cari.trim() ? t('tutorial.empty.searchTitle') : t('tutorial.empty.chapterTitle')}>
                        {cari.trim() ? t('tutorial.empty.searchBody') : t('tutorial.empty.chapterBody')}
                    </EmptyState>
                ) : (
                    <div style={GRID_KAD}>
                        {ditapis.map((v) => {
                            const i = view.videos.indexOf(v);        // indeks sebenar dalam draft
                            const done = watched.has(v.id);
                            const ytId = ytOf(v);
                            const catLabel = (view.categories || []).find(c => c.id === v.category)?.label || '';

                            return (
                                <Card key={v.id} hover={!!ytId && !editing} style={{ display: 'flex', flexDirection: 'column' }}>

                                    <button
                                        type="button"
                                        onClick={() => openVideo(v)}
                                        disabled={!ytId}
                                        aria-label={ytId
                                            ? t('tutorial.a11y.playLesson', { tajuk: v.title })
                                            : t('tutorial.a11y.videoUnavailable')}
                                        style={{
                                            display: 'block', width: '100%', padding: 0, border: 0,
                                            background: 'none', cursor: ytId ? 'pointer' : 'default',
                                        }}
                                    >
                                        <Thumb ytId={ytId} done={done} duration={v.duration} />
                                    </button>

                                    <CardBody style={{ flex: 1 }}>
                                        {editing ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                                    <IconBtn label={t('tutorial.a11y.moveUp')} icon={<ChevronUp size={15} strokeWidth={1.9} />}
                                                        onClick={() => mutate(d => moveItem(d.videos, i, i - 1))} />
                                                    <IconBtn label={t('tutorial.a11y.moveDown')} icon={<ChevronDown size={15} strokeWidth={1.9} />}
                                                        onClick={() => mutate(d => moveItem(d.videos, i, i + 1))} />
                                                    <Btn size="sm" variant={v.isNew ? 'primary' : 'quiet'}
                                                        onClick={() => setVideo(i, 'isNew', !v.isNew)}
                                                        title={t('tutorial.edit.newBadgeTip')}>
                                                        {t('tutorial.edit.newToggle')}
                                                    </Btn>
                                                    <IconBtn
                                                        label={t('tutorial.a11y.deleteVideo')}
                                                        icon={<Trash2 size={15} strokeWidth={1.9} />}
                                                        onClick={() => {
                                                            if (!window.confirm(t('tutorial.confirm.deleteVideo'))) return;
                                                            mutate(d => {
                                                                const [gone] = d.videos.splice(i, 1);
                                                                d.startHere = (d.startHere || []).filter(id => id !== gone.id);
                                                            });
                                                        }}
                                                    />
                                                </div>

                                                <EditBox value={v.title} onChange={(val) => setVideo(i, 'title', val)} placeholder={t('tutorial.edit.lessonTitle')} />
                                                <EditBox value={v.body} onChange={(val) => setVideo(i, 'body', val)} placeholder={t('tutorial.edit.shortDesc')} rows={3} />
                                                <EditBox value={v.youtubeId} mono
                                                    onChange={(val) => setVideo(i, 'youtubeId', parseYouTubeId(val) || val)}
                                                    placeholder={t('tutorial.edit.ytPlaceholder')} />

                                                {/* Butang tindakan pilihan yang keluar di bawah nota
                                                    lesson. Kosongkan label ATAU alamat = tiada butang.
                                                    Alamat dalaman ditulis "/extension"; alamat luar
                                                    ditulis penuh "https://…" dan dibuka tab baharu. */}
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <EditBox value={v.ctaLabel}
                                                        onChange={(val) => setVideo(i, 'ctaLabel', val)}
                                                        placeholder={t('tutorial.edit.ctaLabelPlaceholder')}
                                                        style={{ flex: 1, minWidth: 0 }} />
                                                    {/* ⛔ "/extension" = CONTOH ALAMAT ROUTE, bukan ayat UI —
                                                        jangan terjemah. */}
                                                    <EditBox value={v.ctaHref} mono
                                                        onChange={(val) => setVideo(i, 'ctaHref', val)}
                                                        placeholder="/extension"
                                                        style={{ flex: 1, minWidth: 0 }} />
                                                </div>

                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <select
                                                        value={v.category}
                                                        onChange={(e) => setVideo(i, 'category', e.target.value)}
                                                        style={{ ...selectStyle, flex: 1, minWidth: 0 }}
                                                    >
                                                        {(view.categories || []).map(c => (
                                                            <option key={c.id} value={c.id}>{c.label || t('tutorial.untitledCategory')}</option>
                                                        ))}
                                                    </select>
                                                    <EditBox value={v.duration} onChange={(val) => setVideo(i, 'duration', val)}
                                                        placeholder="4:32" style={{ width: 74 }} />
                                                </div>

                                                {!ytId && (
                                                    <span className="s-meta" style={{ color: 'var(--s-warn)' }}>
                                                        {v.youtubeId
                                                            ? t('tutorial.edit.ytInvalid')
                                                            : t('tutorial.edit.ytMissing')}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 5 }}>
                                                        <h2 className="s-cardname">{v.title || t('tutorial.untitledLesson')}</h2>
                                                        {v.isNew && <Badge tone="brand">{t('tutorial.badge.new')}</Badge>}
                                                    </div>

                                                    <TeksLesson teks={v.body} t={t} style={{ marginBottom: 10 }} />

                                                    {catLabel && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--s-ink-2)' }}>
                                                            <ListVideo size={14} strokeWidth={1.9} />
                                                            <span className="s-meta">{catLabel}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                <IconBtn
                                                    label={done ? t('tutorial.a11y.markUnwatched') : t('tutorial.a11y.markWatched')}
                                                    icon={done
                                                        ? <CircleCheck size={16} strokeWidth={2} />
                                                        : <Circle size={16} strokeWidth={1.9} />}
                                                    onClick={(e) => toggleWatched(e, v)}
                                                />
                                            </div>
                                        )}
                                    </CardBody>

                                    {!editing && (
                                        <CardFoot>
                                            <span className="s-dim">
                                                {!ytId ? t('tutorial.state.comingSoon') : done ? t('tutorial.state.watched') : t('tutorial.state.notWatched')}
                                            </span>
                                            {ytId && (
                                                <Btn size="sm" variant="ghost"
                                                    icon={<Play size={13} strokeWidth={0} fill="currentColor" />}
                                                    onClick={() => openVideo(v)}>
                                                    {done ? t('tutorial.btn.rewatch') : t('tutorial.btn.watch')}
                                                </Btn>
                                            )}
                                        </CardFoot>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* ─────────── KAKI: playlist + group support ─────────── */}
            <section style={{ marginTop: 30 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
                    {[
                        {
                            id: 'playlist',
                            href: view.playlistUrl, Icon: Youtube,
                            title: t('tutorial.foot.playlistTitle'),
                            desc: t('tutorial.foot.playlistDesc'),
                        },
                        {
                            id: 'help',
                            href: view.whatsappUrl, Icon: MessageCircle,
                            title: t('tutorial.foot.helpTitle'),
                            desc: t('tutorial.foot.helpDesc'),
                        },
                    ].map((pautan) => {
                        /* Nama komponen diambil sebagai pemboleh ubah bermula huruf
                           besar, BUKAN destructure dalam senarai parameter: ESLint
                           projek ini tiada `jsx-uses-vars`, jadi nama yang hanya
                           muncul sebagai <Ikon /> tidak dikira digunakan — dan
                           pengecualian huruf besar hanya terpakai pada pemboleh
                           ubah, bukan parameter. Corak sama dengan StudioShell. */
                        const Ikon = pautan.Icon;
                        return (
                            /* key = id tetap, BUKAN tajuk: tajuk kini bertukar
                               mengikut bahasa, dan kunci yang berubah memaksa
                               React memasang semula kad setiap kali suis ditekan. */
                            <a key={pautan.id} href={pautan.href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                <Card hover>
                                    <CardBody style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <span style={{
                                            width: 36, height: 36, borderRadius: 'var(--s-r)', flex: 'none',
                                            display: 'grid', placeItems: 'center',
                                            background: 'var(--s-fill)', color: 'var(--s-ink-2)',
                                        }}>
                                            <Ikon size={17} strokeWidth={1.9} />
                                        </span>
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            <span className="s-cardname" style={{ display: 'block' }}>{pautan.title}</span>
                                            <span className="s-meta" style={{ display: 'block' }}>{pautan.desc}</span>
                                        </span>
                                        <ExternalLink size={15} strokeWidth={1.9} style={{ color: 'var(--s-ink-3)', flex: 'none' }} />
                                    </CardBody>
                                </Card>
                            </a>
                        );
                    })}
                </div>
            </section>

        </TutorialShell>
    );
}
